import React, { useEffect, useState } from 'react';
import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  Flex,
  Label,
  ListItem,
  Radio,
  Text,
} from '@/components/ui';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import {
  loadColumnMappingPreference,
  PreferenceScope,
  saveColumnMappingPreference,
} from '../utils/columnMappingPreferences';

interface ColumnMappingConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnName: string;
  mappingType: 'metadata' | 'formField' | 'ignored' | 'unclassified';
  targetField?: string;
  metadataTypeLabel?: string;
  similarFilesCount: number;
  onConfirm: (applyToAll: boolean) => void;
}

export const ColumnMappingConfirmationModal: React.FC<
  ColumnMappingConfirmationModalProps
> = ({
  open,
  onOpenChange,
  columnName,
  mappingType,
  targetField,
  metadataTypeLabel,
  similarFilesCount,
  onConfirm,
}) => {
  const [rememberChoice, setRememberChoice] = useState(false);
  const [preferenceScope, setPreferenceScope] =
    useState<PreferenceScope>('session');
  const [savedPreference, setSavedPreference] = useState<{
    applyToAll: boolean | null;
    scope: PreferenceScope | null;
  } | null>(null);
  void savedPreference;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      const preference = loadColumnMappingPreference();
      setSavedPreference(preference);
      // Note: If there's a saved preference, the modal shouldn't be shown at all
      // This is handled in the parent component
    } else {
      // Reset state when modal closes
      setRememberChoice(false);
      setPreferenceScope('session');
      setSavedPreference(null);
    }
  }, [open]);

  const getMappingDescription = () => {
    if (mappingType === 'metadata') {
      return (
        <>
          Map column <strong>"{columnName}"</strong> as metadata{' '}
          {metadataTypeLabel && (
            <>
              (<strong>{metadataTypeLabel}</strong>)
            </>
          )}
        </>
      );
    } else if (mappingType === 'formField') {
      return (
        <>
          Map column <strong>"{columnName}"</strong> to form field{' '}
          {targetField && <strong>"{targetField}"</strong>}
        </>
      );
    } else {
      return (
        <>
          Ignore column <strong>"{columnName}"</strong>
        </>
      );
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="large">
        <Dialog.Header
          title="Apply Mapping Change"
          subtitle="This column exists in multiple files"
        />
        <Dialog.Body>
          <Flex direction="column" gap="4">
            <Callout
              icon={<IconAlertCircle />}
              color="info"
              variant="surface"
              title={`Column found in ${similarFilesCount + 1} file(s)`}
            >
              <Text size="2" style={{ color: 'var(--blue-11)' }}>
                {getMappingDescription()}
              </Text>
            </Callout>

            <Text size="2xsmall">Choose how to apply this change:</Text>

            <Flex direction="column" gap="2">
              <Button
                startIcon={<IconCheck />}
                variant="outlined"
                asChild
                onClick={() => {
                  if (rememberChoice) {
                    saveColumnMappingPreference(false, preferenceScope);
                  }
                  onConfirm(false);
                  onOpenChange(false);
                }}
              >
                <ListItem
                  startIcon={<IconCheck />}
                  primary={
                    <Text size="2xsmall" weight="medium" color="textPrimary">
                      Apply to this file only
                    </Text>
                  }
                  secondary={
                    <Text size="3xsmall" color="textSecondary">
                      Change will only affect the current file
                    </Text>
                  }
                />
              </Button>

              <Button
                variant="outlined"
                color="primary"
                asChild
                onClick={() => {
                  if (rememberChoice) {
                    saveColumnMappingPreference(true, preferenceScope);
                  }
                  onConfirm(true);
                  onOpenChange(false);
                }}
                style={{ justifyContent: 'flex-start' }}
                startIcon={<IconCheck />}
              >
                <ListItem
                  startIcon={<IconCheck />}
                  primary={
                    <Text size="2xsmall" weight="medium" color="textPrimary">
                      Apply to all {similarFilesCount + 1} files
                    </Text>
                  }
                  secondary={
                    <Text size="3xsmall" color="textSecondary">
                      Change will affect all files with this column name
                    </Text>
                  }
                />
              </Button>
            </Flex>

            {/* Remember choice option */}
            <>
              <Flex direction="column" gap="2" mt="4">
                <Checkbox
                  checked={rememberChoice}
                  onCheckedChange={checked =>
                    setRememberChoice(checked === true)
                  }
                  label={' Remember my choice'}
                />

                {rememberChoice && (
                  <Flex
                    direction="column"
                    gap="2"
                    style={{ marginLeft: '24px' }}
                  >
                    <Label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <Radio
                        value="session"
                        checked={preferenceScope === 'session'}
                        onChange={() => setPreferenceScope('session')}
                        style={{ marginTop: '2px' }}
                      />
                      <Flex direction="column" gap="1">
                        <Text size="2xsmall" weight="medium">
                          Until I close the browser
                        </Text>
                        <Text size="3xsmall" color="textSecondary">
                          Preference will be remembered during this session
                        </Text>
                      </Flex>
                    </Label>
                    <Label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <Radio
                        value="permanent"
                        checked={preferenceScope === 'permanent'}
                        onChange={() => setPreferenceScope('permanent')}
                        style={{ marginTop: '2px' }}
                      />
                      <Flex direction="column" gap="1">
                        <Text size="2" weight="medium">
                          Remember permanently
                        </Text>
                        <Text size="1" color="textSecondary">
                          Preference will be saved and used for future sessions
                        </Text>
                      </Flex>
                    </Label>
                  </Flex>
                )}
              </Flex>
            </>
          </Flex>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="outlined" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
